import { core, parseDay, type Day } from '@map-my-money/engine';
import { Fragment, useDeferredValue, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { ShareBrand, TemplateData, TemplateItem, WirePlan } from '../api/types.ts';
import { FLOW_COLOR, MonthlyFlowChart, NetWorthMapChart, SERIES_COLOR, leftOver } from '../components/charts/SharedMapCharts.tsx';
import { BrandPin, Icon, type IconName } from '../components/Icon.tsx';
import { Segmented } from '../components/ui.tsx';
import { t, tParts, type MessageKey } from '../i18n/index.ts';
import { axisMoney, compactMoney, fullDate, linePointLabel, longSpan, money, monthYear, percent, setRegion } from '../lib/format.ts';
import { useReducedMotion } from '../lib/hooks.ts';
import { decode } from '../lib/model.ts';
import {
  NO_ADJUSTMENTS, RANGES, isAdjusted, mainMortgage, mapDelta, milestoneMoves, monthAt, monthIndex, safeLabel, sharedMapModel,
  templateModel, type Adjustments, type MapGoal, type MapModel, type MilestoneKind, type ShareAnchor,
} from '../lib/sharedMap.ts';
import { GOALS_HORIZON_YEARS } from '@map-my-money/views';

/** What the page is drawn from: the owner's plan (map) or the indexed preview (template). */
export type SharedMapSource =
  /** `anchor` is the sharer's now on asOf; links shared before it was recorded have none. */
  | { readonly kind: 'map'; readonly asOf: string; readonly plan: WirePlan; readonly anchor: ShareAnchor | null }
  | { readonly kind: 'template'; readonly asOf: string; readonly template: TemplateData };

export interface SharedMapMeta {
  readonly title: string;
  readonly ownerFirstName: string | null;
  readonly expiresAt: string | null;
  /** A Coach's name and accent colour. */
  readonly brand?: ShareBrand | undefined;
}

// MARK: Units: money for maps, multiples of today and shares of income for templates

interface Units {
  readonly value: (v: number) => string;
  readonly signed: (v: number) => string;
  readonly axis: (v: number) => string;
  readonly flow: (v: number) => string;
  readonly flowAxis: (v: number) => string;
}

const trim = (s: string) => s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
const multipleNumber = (indexed: number) => {
  const x = indexed / 100;
  const abs = Math.abs(x);
  return `${x < 0 ? '−' : ''}${trim(abs >= 10 ? abs.toFixed(0) : abs >= 1 ? abs.toFixed(1) : abs.toFixed(2))}`;
};
export const multiple = (indexed: number) => t('smap.times', { x: multipleNumber(indexed) });
const timesToday = (indexed: number) => t('smap.timesToday', { x: multipleNumber(indexed) });

const MONEY_UNITS: Units = {
  value: (v) => compactMoney(v),
  signed: (v) => compactMoney(v, { sign: true }),
  axis: axisMoney,
  flow: (v) => money(v),
  flowAxis: axisMoney,
};
const INDEX_UNITS: Units = {
  value: multiple,
  signed: (v) => `${v >= 0 ? '+' : ''}${multiple(v)}`,
  axis: multiple,
  flow: (v) => percent(v),
  flowAxis: (v) => percent(v),
};

// MARK: Small pieces

const owedText = (units: Units, owed: number, hadDebt: boolean) =>
  owed <= 0.5 ? (hadDebt ? t('smap.paidOff') : t('common.none')) : units.value(-owed);

function sinceLabel(from: Day, to: Day): string {
  const months = core.monthsBetween(core.startOfMonth(from), to);
  if (months <= 0) return t('smap.readout.thisMonth');
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts = [years > 0 ? longSpan(years * 12) : '', rest > 0 ? longSpan(rest) : ''].filter(Boolean);
  return t('smap.readout.in', { span: parts.join(' ') });
}

function moveText(months: number | null): { text: string; tone: 'same' | 'sooner' | 'later' } {
  if (months === null) return { text: t('smap.ms.outOfView'), tone: 'later' };
  if (months === 0) return { text: t('smap.ms.same'), tone: 'same' };
  return months > 0
    ? { text: t('smap.delta.sooner', { span: longSpan(months) }), tone: 'sooner' }
    : { text: t('smap.delta.later', { span: longSpan(-months) }), tone: 'later' };
}

function useCountUp(target: number, run: boolean): number {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(run && !reduced ? 0 : target);
  const started = useRef(false);
  useEffect(() => {
    if (!run || reduced || started.current) {
      setShown(target);
      return;
    }
    started.current = true;
    let frame = 0;
    const begin = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - begin) / 900);
      setShown(target * (1 - (1 - p) ** 3));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, run, reduced]);
  return shown;
}

const SAFE_ACCENT = /^#[0-9a-f]{6}$/i;

/** The accent only ever colours a thin bar and an underline, never text, so any colour keeps the page readable. */
export const brandStyle = (brand: ShareBrand | null | undefined): CSSProperties | undefined =>
  brand && SAFE_ACCENT.test(brand.accent) ? ({ '--brand-accent': brand.accent } as CSSProperties) : undefined;

export function SharedHeader({ owner, brand }: { owner: string | null; brand?: ShareBrand | null | undefined }) {
  return (
    <header className={`smap-top ${brand ? 'smap-top--branded' : ''}`} style={brandStyle(brand)}>
      <a className="smap-top__brand" href="https://mapmymoney.app">
        <BrandPin size={16} />
        <span>{t('app.name')}</span>
      </a>
      <span className="smap-top__by">
        {brand ? (
          <span className="smap-top__coach">{t('smap.brand.sharedBy', { name: brand.name })}</span>
        ) : (
          <span>{owner ? t('smap.sharedBy', { name: owner }) : t('smap.sharedWithYou')}</span>
        )}
        <span className="smap-chip"><Icon name="eye" size={13} />{t('smap.viewOnly')}</span>
      </span>
    </header>
  );
}

export function SharedFooter({ owner, expiresAt }: { owner: string | null; expiresAt: string | null }) {
  const [reported, setReported] = useState(false);
  const expires = expiresAt ? t('smap.footer.expires', { date: fullDate(dateDay(expiresAt)) }) : t('smap.footer.noExpiry');
  const parts = [owner ? t('smap.footer.sharedBy', { name: owner }) : null, expires, t('smap.footer.estimates')].filter(Boolean) as string[];
  return (
    <footer className="smap-footer">
      {parts.map((p) => (
        <Fragment key={p}><span>{p}</span><span className="smap-footer__dot" aria-hidden="true">·</span></Fragment>
      ))}
      {reported ? (
        <span role="status" className="smap-footer__ack">{t('smap.footer.reported')}</span>
      ) : (
        <button type="button" className="smap-footer__report" onClick={() => setReported(true)}>{t('smap.footer.report')}</button>
      )}
    </footer>
  );
}

const dateDay = (iso: string): Day => parseDay(iso.slice(0, 10));

// MARK: The page

export function SharedMapView({ source, meta, cta, preview = false }: {
  source: SharedMapSource;
  meta: SharedMapMeta;
  /** The call to action (card on desktop, dock on phones); omitted in the owner's preview. */
  cta?: ReactNode;
  preview?: boolean;
}) {
  const template = source.kind === 'template';
  const units = template ? INDEX_UNITS : MONEY_UNITS;
  const asOf = parseDay(source.asOf);
  if (source.kind === 'map') setRegion(source.plan.configuration.taxJurisdiction);

  const shared = useMemo<MapModel>(
    () => (source.kind === 'map' ? sharedMapModel(source.plan, asOf, NO_ADJUSTMENTS, source.anchor) : templateModel(source.template.preview, source.asOf)),
    [source],
  );
  const [adjustments, setAdjustments] = useState<Adjustments>(NO_ADJUSTMENTS);
  const deferred = useDeferredValue(adjustments);
  const yours = useMemo(
    () => (source.kind === 'map' && isAdjusted(deferred) ? sharedMapModel(source.plan, asOf, deferred, source.anchor) : shared),
    [source, deferred, shared],
  );
  const changed = yours !== shared;
  const hasMortgage = source.kind === 'map' ? mainMortgage(decode(source.plan), asOf) !== null : (shared.months[0]?.owed ?? 0) > 0;

  const ranges = RANGES.filter((r) => r <= shared.horizonYears);
  const [range, setRange] = useState<number>(Math.min(10, shared.horizonYears));
  const last = monthIndex(shared, range);
  const [index, setIndex] = useState(last);
  const pickRange = (r: number) => {
    setRange(r);
    setIndex(monthIndex(shared, r));
  };

  return (
    <div className={`smap ${preview ? 'smap--preview' : ''}`}>
      <SharedHeader owner={meta.ownerFirstName} brand={meta.brand} />
      {template && (
        <p className="smap-banner">
          <Icon name="receipt" size={15} />
          <span><strong>{t('smap.templateBanner.title')}</strong> {t('smap.templateBanner.body')}</span>
        </p>
      )}
      <Hero meta={meta} model={shared} units={units} template={template} animate={!preview} titleLevel={preview ? 'h2' : 'h1'} />
      <ChartCard
        model={yours}
        shared={changed ? shared : null}
        units={units}
        template={template}
        ranges={ranges}
        range={range}
        onRange={pickRange}
        index={Math.min(index, last)}
        onIndex={setIndex}
        last={last}
      />
      <div className="smap-grid">
        <div className="smap-col">
          <PlayCard
            template={template}
            hasMortgage={hasMortgage}
            adjustments={adjustments}
            onChange={setAdjustments}
            shared={shared}
            yours={yours}
            units={units}
          />
          <FlowCard model={yours} units={units} template={template} />
          {source.kind === 'template' && <StructureCard items={source.template.structure} asOf={asOf} />}
        </div>
        <div className="smap-col">
          <MilestonesCard shared={shared} yours={yours} template={template} />
          {shared.goals && shared.goals.length > 0 && <GoalsCard goals={shared.goals} />}
          <KeyNumbers shared={shared} yours={yours} units={units} template={template} changed={changed} />
          {cta && <OwnCopy />}
          {cta}
        </div>
      </div>
      <SharedFooter owner={meta.ownerFirstName} expiresAt={meta.expiresAt} />
    </div>
  );
}

function growthPhrase(ratio: number): string {
  if (ratio >= 1.9 && ratio < 2.15) return t('smap.growth.doubles');
  if (ratio >= 2.85 && ratio < 3.15) return t('smap.growth.triples');
  if (ratio >= 1.5) return t('smap.growth.times', { x: trim(ratio.toFixed(1)) });
  if (ratio >= 1) return t('smap.growth.percent', { pct: percent((ratio - 1) * 100) });
  return t('smap.growth.falls', { pct: percent((1 - ratio) * 100) });
}

function Hero({ meta, model, units, template, animate, titleLevel: Title }: {
  meta: SharedMapMeta; model: MapModel; units: Units; template: boolean; animate: boolean; titleLevel: 'h1' | 'h2';
}) {
  const titleId = useId();
  const today = model.months[0]?.netWorth ?? 0;
  const years = Math.min(10, model.horizonYears);
  const later = monthAt(model, years)?.netWorth ?? today;
  const shownToday = useCountUp(today, animate);
  const shownLater = useCountUp(later, animate);
  const mortgage = model.milestones.find((m) => m.kind === 'mortgageFree');
  const goal = model.milestones.find((m) => m.kind === 'goal' || m.kind === 'retire');
  const strong = (v: string, name: string) => <strong key={name}>{v}</strong>;
  return (
    <section className="smap-hero card card--bento" aria-labelledby={titleId}>
      <div className="smap-hero__text">
        <Title className="smap-hero__title" id={titleId}>{meta.title}</Title>
        {!template && (
          <p className="smap-asof" data-testid="smap-as-of">
            <strong className="smap-asof__date">{t('smap.asOf', { date: fullDate(model.asOf) })}</strong>
            <span className="smap-asof__note">{meta.ownerFirstName ? t('smap.asOf.note', { name: meta.ownerFirstName }) : t('smap.asOf.noteAnon')}</span>
          </p>
        )}
        <p className="smap-hero__meta">
          <span className="smap-hero__nosignin"><Icon name="check" size={13} strokeWidth={2.5} />{t('smap.noSignIn')}</span>
          <span>{template ? t('smap.meta', { years: model.horizonYears, date: fullDate(model.asOf) }) : t('smap.meta.horizon', { years: model.horizonYears })}</span>
        </p>
        {today > 0 && (
          <p className="smap-hero__story">
            {tParts('smap.story.growth', { growth: growthPhrase(later / today), years }, strong)}
            {mortgage && tParts('smap.story.mortgage', { date: monthYear(mortgage.date) }, strong)}
            {goal && tParts('smap.story.goal', { goal: goal.label, date: monthYear(goal.date) }, strong)}
            {t('smap.story.end')}
          </p>
        )}
      </div>
      <dl className="smap-hero__stats">
        <div>
          <dt>{template ? t('smap.netWorthToday') : t('smap.netWorthOn', { date: fullDate(model.asOf) })}</dt>
          {template ? (
            <dd className="smap-hero__placeholder">{t('smap.yourNumber')}</dd>
          ) : (
            <dd className="hero-amount smap-hero__amount" aria-label={units.value(today)}>{units.value(shownToday)}</dd>
          )}
        </div>
        <div>
          <dt>{t('smap.inYears', { n: years })}</dt>
          <dd className="hero-amount smap-hero__amount text-mint" aria-label={template ? timesToday(later) : units.value(later)}>
            {template ? timesToday(shownLater) : units.value(shownLater)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

const SPLIT = [
  { key: 'cash', label: 'smap.series.cash' },
  { key: 'investments', label: 'smap.series.investments' },
  { key: 'home', label: 'smap.series.home' },
  { key: 'owed', label: 'smap.series.owed' },
] as const satisfies readonly { key: keyof typeof SERIES_COLOR; label: MessageKey }[];

function ChartCard({ model, shared, units, template, ranges, range, onRange, index, onIndex, last }: {
  model: MapModel;
  shared: MapModel | null;
  units: Units;
  template: boolean;
  ranges: readonly number[];
  range: number;
  onRange: (r: number) => void;
  index: number;
  onIndex: (i: number) => void;
  last: number;
}) {
  const id = useId();
  const [table, setTable] = useState(false);
  const months = model.months.slice(0, last + 1);
  const sharedMonths = shared?.months.slice(0, last + 1) ?? null;
  const end = months[months.length - 1]?.date ?? model.asOf;
  const milestones = model.milestones.filter((m) => m.date <= end);
  const at = months[index] ?? months[months.length - 1]!;
  const hadDebt = (model.months[0]?.owed ?? 0) > 0;
  const [compact, setCompact] = useState(false);
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setCompact((entry?.contentRect.width ?? 800) < 560));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const valueText = (i: number) => {
    const m = months[i];
    return m ? t('smap.scrubber.value', { date: linePointLabel(m.date, i === 0), value: units.value(m.netWorth) }) : '';
  };
  const yearly = months.filter((m, i) => i === 0 || core.toYmd(m.date).month === 12);
  return (
    <section className="card smap-card smap-chart-card" aria-labelledby={id} ref={frame}>
      <div className="smap-card__head">
        <div>
          <h2 className="smap-card__title" id={id}>{t('smap.chart.title')}</h2>
          <p className="smap-card__sub">{template ? t('smap.chart.hintTemplate') : t('smap.chart.hint')}</p>
        </div>
        {ranges.length > 1 && (
          <Segmented
            label={t('smap.range')}
            variant="pill"
            options={ranges.map((r) => ({ value: r, label: t('smap.range.option', { n: r }) }))}
            value={range}
            onChange={onRange}
          />
        )}
      </div>
      <div className="smap-readout" aria-live="polite">
        <div className="smap-readout__main">
          <span className="smap-readout__date">
            {linePointLabel(at.date, at.date === model.asOf)}
            {at.date !== model.asOf && <span className="muted"> · {sinceLabel(model.asOf, at.date)}</span>}
          </span>
          <span className="smap-readout__value">{units.value(at.netWorth)}</span>
        </div>
        <dl className="smap-readout__split">
          {SPLIT.map((s) => (
            <div key={s.key}>
              <dt><i style={{ background: SERIES_COLOR[s.key] }} aria-hidden="true" />{t(s.label)}</dt>
              <dd>{s.key === 'owed' ? owedText(units, at.owed, hadDebt) : units.value(at[s.key])}</dd>
            </div>
          ))}
        </dl>
      </div>
      <NetWorthMapChart
        months={months}
        shared={sharedMonths}
        milestones={milestones}
        index={index}
        onIndex={onIndex}
        height={compact ? 260 : 360}
        axis={units.axis}
        valueText={valueText}
        compact={compact}
      />
      <ul className="legend smap-legend">
        <li><span className="legend__swatch legend__swatch--line" style={{ background: SERIES_COLOR.netWorth }} aria-hidden="true" />{t('smap.series.netWorth')}</li>
        {shared && <li><span className="legend__dash" aria-hidden="true" />{t('smap.series.shared')}</li>}
        {SPLIT.map((s) => (
          <li key={s.key}><span className="legend__swatch" style={{ background: SERIES_COLOR[s.key] }} aria-hidden="true" />{t(s.label)}</li>
        ))}
        {milestones.length > 0 && <li><BrandPin size={10} />{t('smap.series.milestone')}</li>}
      </ul>
      <button type="button" className="smap-link" aria-expanded={table} onClick={() => setTable((v) => !v)}>
        {table ? t('smap.table.hide') : t('smap.table.show')}
      </button>
      {table && (
        <div className="table-scroll">
          <table className="data-table smap-table">
            <caption className="sr-only">{t('smap.table.netWorth')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('smap.table.date')}</th>
                <th scope="col" className="num">{t('smap.series.netWorth')}</th>
                {SPLIT.map((s) => <th key={s.key} scope="col" className="num">{t(s.label)}</th>)}
              </tr>
            </thead>
            <tbody>
              {yearly.map((m, i) => (
                <tr key={m.date}>
                  <th scope="row">{linePointLabel(m.date, i === 0)}</th>
                  <td className="num">{units.value(m.netWorth)}</td>
                  {SPLIT.map((s) => <td key={s.key} className="num">{s.key === 'owed' ? owedText(units, m.owed, hadDebt) : units.value(m[s.key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const SLIDERS = [
  { key: 'spendLess', label: 'smap.play.spend', plain: 'smap.play.spendPlain', max: 1000, step: 25, tone: 'amber' },
  { key: 'investMore', label: 'smap.play.invest', plain: 'smap.play.investPlain', max: 1500, step: 25, tone: 'violet' },
  { key: 'overpay', label: 'smap.play.overpay', plain: 'smap.play.overpayPlain', max: 1000, step: 25, tone: 'terracotta' },
] as const satisfies readonly { key: keyof Adjustments; label: MessageKey; plain: MessageKey; max: number; step: number; tone: string }[];

function PlayCard({ template, hasMortgage, adjustments, onChange, shared, yours, units }: {
  template: boolean;
  hasMortgage: boolean;
  adjustments: Adjustments;
  onChange: (a: Adjustments) => void;
  shared: MapModel;
  yours: MapModel;
  units: Units;
}) {
  const id = useId();
  const noteId = useId();
  const sliders = SLIDERS.filter((s) => s.key !== 'overpay' || hasMortgage);
  return (
    <section className="card smap-card" aria-labelledby={id}>
      <h2 className="smap-card__title" id={id}>{t('smap.play.title')}</h2>
      <p className="smap-card__sub">{t('smap.play.sub')}</p>
      {template && <p className="smap-note smap-note--locked" id={noteId}><Icon name="lock" size={14} />{t('smap.play.disabled')}</p>}
      <div className="smap-sliders">
        {sliders.map((s) => {
          const value = adjustments[s.key];
          const inputId = `${id}-${s.key}`;
          const amount = money(value);
          return (
            <div key={s.key} className="smap-slider">
              <label htmlFor={inputId} className="smap-slider__label">
                {template ? t(s.plain) : tParts(s.label, { amount }, (v) => <strong key="a" className={value ? '' : 'muted'}>{v}</strong>)}
              </label>
              <input
                id={inputId}
                type="range"
                className={`slider slider--${s.tone}`}
                min={0}
                max={s.max}
                step={s.step}
                value={value}
                disabled={template}
                aria-valuetext={template ? t('smap.play.disabled') : amount}
                {...(template ? { 'aria-describedby': noteId } : {})}
                onChange={(e) => onChange({ ...adjustments, [s.key]: Number(e.target.value) })}
                style={{ ['--fill' as string]: `${(value / s.max) * 100}%` }}
              />
            </div>
          );
        })}
      </div>
      {!template && <DeltaPanel shared={shared} yours={yours} units={units} adjusted={isAdjusted(adjustments)} />}
      <div className="smap-play__foot">
        <p className="smap-note"><Icon name="shield" size={14} />{t('smap.play.private')}</p>
        {!template && (
          <button type="button" className="button button--sm button--outline" disabled={!isAdjusted(adjustments)} onClick={() => onChange(NO_ADJUSTMENTS)}>
            {t('common.reset')}
          </button>
        )}
      </div>
    </section>
  );
}

function DeltaPanel({ shared, yours, units, adjusted }: { shared: MapModel; yours: MapModel; units: Units; adjusted: boolean }) {
  const delta = mapDelta(shared, yours);
  const same = !adjusted || yours === shared;
  const nwTone = delta.netWorth10 >= 0 ? 'text-mint' : 'text-amber';
  const mortgage = delta.mortgageFree;
  return (
    <div className="smap-delta" aria-live="polite" data-testid="delta">
      <div className="smap-delta__head">
        <span>{t('smap.delta.title')}</span>
        <span className={`smap-badge ${same ? '' : 'smap-badge--mint'}`}>
          {!same && <Icon name="check" size={12} strokeWidth={2.5} />}
          {same ? t('smap.delta.same') : t('smap.delta.yours')}
        </span>
      </div>
      <dl>
        <div>
          <dt>{t('smap.delta.netWorth')}</dt>
          <dd className={same ? 'muted' : nwTone}>{same ? t('common.noChange') : units.signed(delta.netWorth10)}</dd>
        </div>
        {mortgage && (
          <div>
            <dt>{t('smap.delta.mortgageFree')}</dt>
            <dd>
              {same || mortgage.months === 0
                ? (mortgage.date ? monthYear(mortgage.date) : t('smap.delta.outOfView'))
                : mortgage.date
                  ? t('smap.delta.dateMove', { date: monthYear(mortgage.date), move: moveText(mortgage.months).text })
                  : t('smap.delta.outOfView')}
            </dd>
          </div>
        )}
        <div>
          <dt>{t('smap.delta.biggest')}</dt>
          <dd>{same || !delta.biggest ? t('common.noChange') : t('smap.delta.moved', { label: delta.biggest.milestone.label, move: moveText(delta.biggest.months).text })}</dd>
        </div>
      </dl>
    </div>
  );
}

function FlowCard({ model, units, template }: { model: MapModel; units: Units; template: boolean }) {
  const id = useId();
  const [table, setTable] = useState(false);
  const flows = model.flows;
  const first = flows[0];
  const lastFlow = flows[flows.length - 1];
  if (!first || !lastFlow) return null;
  const legend: { key: keyof typeof FLOW_COLOR; label: string; mark: 'box' | 'line' | 'dot' }[] = [
    { key: 'income', label: t('smap.flow.income'), mark: 'line' },
    { key: 'spending', label: template ? t('smap.flow.spendingAll') : t('smap.flow.spending'), mark: 'box' },
    ...(template ? [] : [{ key: 'debt' as const, label: t('smap.flow.debt'), mark: 'box' as const }]),
    { key: 'invested', label: t('smap.flow.invested'), mark: 'box' },
    { key: 'left', label: t('smap.flow.left'), mark: 'dot' },
  ];
  return (
    <section className="card smap-card" aria-labelledby={id}>
      <h2 className="smap-card__title" id={id}>{t('smap.flow.title')}</h2>
      <p className="smap-card__sub">{template ? t('smap.flow.subTemplate') : t('smap.flow.sub')}</p>
      <MonthlyFlowChart
        flows={flows}
        height={200}
        axis={units.flowAxis}
        value={units.flow}
        label={t('smap.flow.label', { from: monthYear(first.date), to: monthYear(lastFlow.date) })}
      />
      <ul className="legend smap-legend">
        {legend.map((l) => (
          <li key={l.key}>
            <span
              className={`legend__swatch ${l.mark === 'line' ? 'legend__swatch--line' : l.mark === 'dot' ? 'smap-legend__dot' : ''}`}
              style={l.mark === 'dot' ? { borderColor: FLOW_COLOR.left } : { background: FLOW_COLOR[l.key] }}
              aria-hidden="true"
            />
            {l.label}
          </li>
        ))}
      </ul>
      <button type="button" className="smap-link" aria-expanded={table} onClick={() => setTable((v) => !v)}>
        {table ? t('smap.table.hide') : t('smap.table.show')}
      </button>
      {table && (
        <div className="table-scroll">
          <table className="data-table smap-table">
            <caption className="sr-only">{t('smap.flow.title')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('smap.flow.month')}</th>
                <th scope="col" className="num">{t('smap.flow.income')}</th>
                <th scope="col" className="num">{template ? t('smap.flow.spendingAll') : t('smap.flow.spending')}</th>
                {!template && <th scope="col" className="num">{t('smap.flow.debt')}</th>}
                <th scope="col" className="num">{t('smap.flow.invested')}</th>
                <th scope="col" className="num">{t('smap.flow.left')}</th>
              </tr>
            </thead>
            <tbody>
              {flows.map((f) => (
                <tr key={f.date}>
                  <th scope="row">{monthYear(f.date)}</th>
                  <td className="num">{units.flow(f.income)}</td>
                  <td className="num">{units.flow(f.spending)}</td>
                  {!template && <td className="num">{units.flow(f.debt ?? 0)}</td>}
                  <td className="num">{units.flow(f.invested)}</td>
                  <td className="num">{units.flow(leftOver(f))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const MEANING: Record<MilestoneKind, MessageKey> = {
  goal: 'smap.ms.meaning.goal', mortgageFree: 'smap.ms.meaning.mortgageFree', debtFree: 'smap.ms.meaning.debtFree', retire: 'smap.ms.meaning.retire',
};

function MilestonesCard({ shared, yours, template }: { shared: MapModel; yours: MapModel; template: boolean }) {
  const id = useId();
  const moves = [...milestoneMoves(shared, yours).values()].sort(
    (a, b) => (a.months === null ? 1 : 0) - (b.months === null ? 1 : 0) || a.milestone.date - b.milestone.date,
  );
  return (
    <section className="card smap-card" aria-labelledby={id}>
      <h2 className="smap-card__title" id={id}>{t('smap.ms.title')}</h2>
      <p className="smap-card__sub">{template ? t('smap.ms.subTemplate') : t('smap.ms.sub')}</p>
      {moves.length === 0 ? (
        <p className="muted">{t('smap.ms.none', { years: shared.horizonYears })}</p>
      ) : (
        <ol className="smap-timeline">
          {moves.map(({ milestone, months }) => {
            const move = moveText(months);
            return (
              <li key={milestone.key} className="smap-timeline__item">
                <span className="smap-timeline__pin" aria-hidden="true"><BrandPin size={14} /></span>
                <div className="smap-timeline__card">
                  <div className="smap-timeline__top">
                    <span className="smap-timeline__date">{monthYear(milestone.date)}</span>
                    {!template && <span className={`smap-move smap-move--${move.tone}`}>{move.text}</span>}
                  </div>
                  <span className="smap-timeline__label">{milestone.label}</span>
                  <span className="smap-timeline__meaning">{t(MEANING[milestone.kind])}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** Every goal on the sharer's line on its day, over the one 50-year horizon, with the same wording as the app. */
function GoalsCard({ goals }: { goals: readonly MapGoal[] }) {
  const id = useId();
  return (
    <section className="card smap-card" aria-labelledby={id} data-testid="smap-goals">
      <h2 className="smap-card__title" id={id}>{t('smap.goals.title')}</h2>
      <ul className="smap-goals">
        {goals.map((g) => (
          <li key={g.id} className="smap-goals__item">
            <span className="smap-goals__label">{g.label}</span>
            <span className="smap-goals__when">
              {g.reached ? t('goals.achieved') : g.date === null ? t('goals.beyond', { years: GOALS_HORIZON_YEARS }) : t('goals.onCourse', { date: monthYear(g.date) })}
            </span>
            <span className="smap-goals__pct">{percent(g.progressPercent, { digits: 1 })}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function savedShare(model: MapModel): number | null {
  const income = model.flows.reduce((s, f) => s + f.income, 0);
  if (income <= 0) return null;
  return (model.flows.reduce((s, f) => s + f.invested + Math.max(0, leftOver(f)), 0) / income) * 100;
}

function KeyNumbers({ shared, yours, units, template, changed }: { shared: MapModel; yours: MapModel; units: Units; template: boolean; changed: boolean }) {
  const id = useId();
  const rows: { label: string; value: string; was?: string }[] = [];
  const at = (m: MapModel, y: number) => monthAt(m, y)?.netWorth ?? 0;
  const value = (v: number) => (template ? timesToday(v) : units.value(v));
  if (!template) rows.push({ label: t('smap.key.on', { date: fullDate(yours.asOf) }), value: units.value(yours.months[0]?.netWorth ?? 0) });
  for (const y of [5, 10, 20].filter((y) => y <= yours.horizonYears)) {
    const row: { label: string; value: string; was?: string } = { label: t('smap.key.inYears', { n: y }), value: value(at(yours, y)) };
    if (changed && Math.round(at(yours, y)) !== Math.round(at(shared, y))) row.was = t('smap.key.was', { value: value(at(shared, y)) });
    rows.push(row);
  }
  const saved = savedShare(yours);
  if (saved !== null) rows.push({ label: t('smap.key.saved'), value: percent(saved) });
  if ((shared.months[0]?.owed ?? 0) > 0) {
    const mine = yours.milestones.find((m) => m.kind === 'mortgageFree');
    const theirs = shared.milestones.find((m) => m.kind === 'mortgageFree');
    const row: { label: string; value: string; was?: string } = {
      label: t('smap.key.mortgageFree'),
      value: mine ? monthYear(mine.date) : t('smap.key.notInView', { n: yours.horizonYears }),
    };
    if (changed && theirs && mine?.date !== theirs.date) row.was = t('smap.key.was', { value: monthYear(theirs.date) });
    rows.push(row);
  }
  return (
    <section className="card smap-card" aria-labelledby={id}>
      <h2 className="smap-card__title" id={id}>{t('smap.key.title')}</h2>
      <dl className="key-numbers">
        {rows.map((r) => (
          <div key={r.label} className="key-numbers__row">
            <dt>{r.label}</dt>
            <dd>
              {r.value}
              {r.was && <span className="smap-was">{r.was}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const ASK: readonly MessageKey[] = ['smap.own.ask.1', 'smap.own.ask.2', 'smap.own.ask.3', 'smap.own.ask.4', 'smap.own.ask.5', 'smap.own.ask.6'];
const TALK: readonly MessageKey[] = ['smap.own.talk.1', 'smap.own.talk.2', 'smap.own.talk.3'];
const ANYWHERE = [
  { icon: 'chat', label: 'smap.own.anywhere.chat' },
  { icon: 'monitor', label: 'smap.own.anywhere.web' },
  { icon: 'phone', label: 'smap.own.anywhere.phone' },
] as const satisfies readonly { icon: IconName; label: MessageKey }[];

/** What a copy of this plan can do once it is someone's own. Fixed examples: nothing here comes from the share. */
function OwnCopy() {
  const id = useId();
  return (
    <section className="card smap-card smap-own" aria-labelledby={id} data-testid="own-copy">
      <h2 className="smap-card__title" id={id}>{t('smap.own.title')}</h2>
      <p className="smap-card__sub">{t('smap.own.sub')}</p>
      <div className="smap-own__part">
        <h3 className="smap-own__head"><Icon name="chat" size={15} />{t('smap.own.ask.title')}</h3>
        <ul className="smap-own__asks">
          {ASK.map((key) => <li key={key} className="smap-own__ask">{t(key)}</li>)}
        </ul>
      </div>
      <div className="smap-own__part">
        <h3 className="smap-own__head"><Icon name="mic" size={15} />{t('smap.own.talk.title')}</h3>
        <ul className="smap-own__says">
          {TALK.map((key) => <li key={key} className="smap-own__say">“{t(key)}”</li>)}
        </ul>
        <p className="smap-own__note"><Icon name="checkCircle" size={14} />{t('smap.own.talk.note')}</p>
      </div>
      <div className="smap-own__part">
        <h3 className="smap-own__head"><Icon name="sparkle" size={15} />{t('smap.own.anywhere.title')}</h3>
        <ul className="smap-own__places">
          {ANYWHERE.map((p) => <li key={p.label}><Icon name={p.icon} size={16} />{t(p.label)}</li>)}
        </ul>
        <p className="smap-own__note smap-own__note--plain">{t('smap.own.anywhere.body')}</p>
      </div>
    </section>
  );
}

const TYPE_LABEL: Record<string, MessageKey> = {
  income: 'plan.type.income', expense: 'plan.type.expense', investment: 'plan.type.investment', debt: 'plan.type.debt',
  property: 'plan.type.property', vehicle: 'plan.type.vehicle',
};
const TYPE_TONE: Record<string, string> = { income: 'mint', expense: 'amber', investment: 'violet', debt: 'terracotta', property: 'violet', vehicle: 'violet' };
const FREQ = new Set(['oneTime', 'daily', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly']);

function StructureCard({ items, asOf }: { items: readonly TemplateItem[]; asOf: Day }) {
  const id = useId();
  return (
    <section className="card smap-card" aria-labelledby={id}>
      <h2 className="smap-card__title" id={id}>{t('smap.structure.title')}</h2>
      <p className="smap-card__sub">{t('smap.structure.sub')}</p>
      <ul className="smap-structure">
        {items.map((item, i) => {
          const type = t(TYPE_LABEL[item.type] ?? 'plan.type.asset');
          const freq = typeof item.frequency === 'string' && FREQ.has(item.frequency) ? t(`plan.freq.${item.frequency}` as MessageKey) : null;
          const start = item.startDate ? parseDay(item.startDate.slice(0, 10)) : null;
          const meta = [
            freq,
            item.paymentDayOfMonth ? t('smap.structure.payDay', { n: item.paymentDayOfMonth }) : null,
            start ? (start > asOf ? t('smap.structure.from', { date: monthYear(start) }) : t('smap.structure.since', { date: monthYear(start) })) : null,
          ].filter(Boolean).join(' · ');
          return (
            <li key={`${item.label}-${i}`} className="smap-structure__item">
              <span className={`dot dot--${TYPE_TONE[item.type] ?? 'slate'}`} aria-hidden="true" />
              <span className="smap-structure__text">
                <span className="smap-structure__label">{safeLabel(item.label, type)}</span>
                {meta && <span className="smap-structure__meta">{meta}</span>}
              </span>
              <span className="smap-structure__type">{type}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

